import Box from '@mui/material/Box';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import theme from 'src/styles/theme';
import { ConfirmationCodeInput } from '../../ConfirmationCodeInput';
import type { AuthenticationFlowPageProps } from '../AuthenticationFlow';
import { RootState } from '../store';
import { markConfirmedPhone } from '../store/authSlice';
import type { StoredPhoneNumberData } from '../store/pageDataTypes';

type AuthenticationEnterConfirmationCodePageProps =
  AuthenticationFlowPageProps & {
    fromGetUpdates?: boolean;
    closeModal?: () => void;
  };

export type AuthenticationEnterConfirmationCodePageUserData = {
  hasValidatedPhoneNumber: boolean;
  validatedPhoneNumber: StoredPhoneNumberData;
};

const verificationCodeLength = 6;

export function AuthenticationEnterConfirmationCodePage({
  pageId: pageID,
  onPageComplete,
  fromGetUpdates = false,
  closeModal,
}: AuthenticationEnterConfirmationCodePageProps) {
  const [pageData, setPageData] = useState<{ confirmationCode?: string }>({
    confirmationCode: undefined,
  });
  const [shouldShowError, setShouldShowError] = useState(false);
  const hasAlreadyValidatedPhoneNumber = useSelector(
    (state: RootState) =>
      state['enter confirmation code'].hasValidatedPhoneNumber,
  );
  const validatedPhoneNumber = useSelector(
    (state: RootState) => state['enter confirmation code'].validatedPhoneNumber,
  );
  const dispatch = useDispatch();

  const nextPage = useCallback(() => {
    onPageComplete(pageID);
  }, [onPageComplete, pageID]);

  // If this phone number has already gotten a confirmation code and we've returned to this page with the
  // phone number unchanged, we can just move on - we don't have to validate it again. This has the effect
  // of just skipping this page, and, although the current auth flow doesn't really lead to the situation
  // where this can happen because you can't go back after finishing a phone registration, if we ever change
  // the flow so that this situation could arise it should make things less annoying for the users
  if (hasAlreadyValidatedPhoneNumber) nextPage();

  const onCodeChange = (enteredCode: string) => {
    const cleanedCode = enteredCode.replace(/[^0-9]/g, '');
    setPageData({ confirmationCode: cleanedCode });
  };

  const attemptSubmit = useCallback(async () => {
    if (
      !pageData.confirmationCode ||
      pageData.confirmationCode.replace(/[^0-9]/g, '').length !==
        verificationCodeLength
    )
      setShouldShowError(true);
    else {
      const { checkVerificationCode } = await import(
        '../../../firebaseCloud/twilio/checkVerificationCode'
      );
      const result = await checkVerificationCode({
        code: pageData.confirmationCode,
        phoneNumber: `+${validatedPhoneNumber.countryDialCode}${validatedPhoneNumber.phoneNumber}`,
      });
      if (result?.data === 'approved') {
        dispatch(markConfirmedPhone());
        setShouldShowError(false);
        if (fromGetUpdates && closeModal) {
          return closeModal();
        }
        return nextPage();
      } else {
        setShouldShowError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPage, pageData, dispatch, validatedPhoneNumber]);

  // useEffect(() => {
  //   if (!!pageData.confirmationCode) {
  //     const cleanedCode = pageData.confirmationCode.replace(/[^0-9]/g, '');
  //     if (cleanedCode.length === verificationCodeLength) {
  //       attemptSubmit();
  //     }
  //   }
  // }, [pageData.confirmationCode, attemptSubmit]);

  if (!pageID || !onPageComplete) return null;

  return (
    <Stack spacing={'32px'} my={'64px'} width={'100%'} maxWidth={'420px'}>
      <Box>
        <Typography
          variant="h4"
          fontWeight="500"
          textTransform={'capitalize'}
          textAlign={'center'}
        >
          Enter Confirmation Code
        </Typography>
      </Box>
      <ConfirmationCodeInput
        onCodeChange={onCodeChange}
        verificationCodeLength={verificationCodeLength}
      />
      <Box textAlign={'center'}>
        {shouldShowError && (
          <Typography
            variant="body2"
            color={theme.palette.error.main}
            textAlign={'center'}
            mb={2}
          >
            Invalid confirmation code, please try again.
          </Typography>
        )}
        <LoadingButton variant="contained" size="large" onClick={attemptSubmit}>
          Submit Code
        </LoadingButton>
      </Box>
    </Stack>
  );
}
